import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLang } from '../lib/lang';
import { getMyEntries, updateProfile, applyReferral, getMyWins, getNotifications, markNotificationRead, getTransactions, markNotificationCelebrated, getMyPayments, getMySubscription, cancelMySubscription } from '../lib/api';
import Footer from '../components/Footer';
import InlineEditable from '../components/InlineEditable';
import PhoneInput from '../components/PhoneInput';
import WinnerCelebrationModal from '../components/WinnerCelebrationModal';
import TicketsPopupDialog from '../components/TicketsPopupDialog';
import TestimonialPromptDialog from '../components/TestimonialPromptDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { User, Trophy, Settings, Ticket, Wallet, Crown, UserPlus, Bell, Gift, History, ArrowUpRight, ArrowDownRight, Coins, Sparkles, Copy, Check, LogOut, CreditCard, UserCog } from 'lucide-react';

export default function AccountPage() {
  const { user, refreshUser, logout, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [wins, setWins] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('entries');
  const [profileForm, setProfileForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [referralInput, setReferralInput] = useState('');
  const [applyingReferral, setApplyingReferral] = useState(false);

  const [copied, setCopied] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [ticketsDialog, setTicketsDialog] = useState({ open: false, entryId: null });
  const [testimonialPrompt, setTestimonialPrompt] = useState({ open: false, winner: null });
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  const handleCancelSubscription = async () => {
    if (!window.confirm(t('confirmCancelSubscription') || 'Êtes-vous sûr de vouloir annuler votre abonnement VIP ? Vous garderez vos avantages jusqu\'à la fin de la période en cours.')) {
      return;
    }
    setCancellingSubscription(true);
    try {
      await cancelMySubscription();
      toast.success(t('subscriptionCancelled') || 'Abonnement annulé');
      // Reload subscription
      const sub = await getMySubscription();
      setSubscription(sub.data.subscription || null);
      if (refreshUser) refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur d\'annulation');
    } finally {
      setCancellingSubscription(false);
    }
  };

  useEffect(() => {
    // Wait for auth to finish loading before redirecting
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    setProfileForm({ first_name: user.first_name || '', last_name: user.last_name || '', phone: user.phone || '', address: user.address || '', city: user.city || '', postcode: user.postcode || '', country: user.country || '' });
    async function load() {
      try {
        const [e, w, n, tx, pmts, sub] = await Promise.all([getMyEntries(), getMyWins(), getNotifications(), getTransactions(), getMyPayments(), getMySubscription()]);
        setEntries(e.data.entries || []);
        setWins(w.data.wins || []);
        setNotifications(n.data.notifications || []);
        setTransactions(tx.data.transactions || []);
        setPayments(pmts.data.transactions || []);
        setSubscription(sub.data.subscription || null);

        // Trigger spectacular celebration popup if there's an unread/uncelebrated winner_announcement
        const winnerNotif = (n.data.notifications || []).find(
          (notif) => notif.type === 'winner_announcement' && !notif.celebrated
        );
        if (winnerNotif) {
          setCelebration(winnerNotif);
        }

        // Detect winners that need testimonial submission
        // (status=published OR null/legacy but no testimonial submitted by user)
        const wins = w.data.wins || [];
        const needsTestimonial = wins.find((win) => {
          // Winner needs testimonial if:
          // - no user_testimonial submitted yet, OR
          // - testimonial was rejected (needs rewrite)
          const status = win.testimonial_status;
          return !win.user_testimonial || status === 'rejected';
        });
        if (needsTestimonial && !winnerNotif) {
          // Don't show both popups at once — celebration first, then testimonial
          setTestimonialPrompt({ open: true, winner: needsTestimonial });
        } else if (needsTestimonial && winnerNotif) {
          // Schedule testimonial AFTER celebration is closed
          // (handled by handleCloseCelebration)
        }
      }
      catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, [user, navigate, authLoading]);

  const handleCloseCelebration = async () => {
    if (celebration) {
      try {
        await markNotificationCelebrated(celebration._id);
        setNotifications((prev) => prev.map((n) => n._id === celebration._id ? { ...n, celebrated: true } : n));
      } catch {}
    }
    setCelebration(null);
    // Show testimonial prompt after celebration if pending
    const pendingTesti = wins.find((win) => !win.user_testimonial || win.testimonial_status === 'rejected');
    if (pendingTesti) {
      setTimeout(() => setTestimonialPrompt({ open: true, winner: pendingTesti }), 600);
    }
  };

  const handleTestimonialSubmitted = async () => {
    setTestimonialPrompt({ open: false, winner: null });
    // Refetch wins to update status
    try {
      const w = await getMyWins();
      setWins(w.data.wins || []);
    } catch {}
  };

  const handleSave = async () => {
    setSaving(true);
    try { await updateProfile(profileForm); await refreshUser(); toast.success(t('profileUpdated')); setEditMode(false); }
    catch { toast.error('Failed to update profile'); }
    finally { setSaving(false); }
  };

  const profileIncomplete = !!user && (!user.phone || !user.address || !user.city || !user.postcode);

  const handleApplyReferral = async () => {
    if (!referralInput.trim()) return;
    setApplyingReferral(true);
    try {
      const res = await applyReferral({ code: referralInput.trim() });
      toast.success(res.data.message);
      setReferralInput('');
      await refreshUser();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply referral'); }
    finally { setApplyingReferral(false); }
  };

  const copyReferral = () => {
    if (!user.referral_code) return;
    navigator.clipboard.writeText(user.referral_code);
    setCopied(true);
    toast.success(t('copiedToClipboard') || 'Copied!');
    setTimeout(() => setCopied(false), 1800);
  };

  const vipLabels = [t('memberLevelStandard') || 'Standard', t('memberLevelBronze') || 'Bronze', t('memberLevelSilver') || 'Silver', t('memberLevelGold') || 'Gold'];

  // Map backend plan id to localised name + benefits (matches VipPage planTranslations)
  const subscriptionPlanTranslations = {
    vip_monthly: {
      name: t('vipMonthlyName') || 'VIP Mensuel',
      benefits: [t('vipBenefitMonthly1'), t('vipBenefitPriority'), t('vipBenefitExclusive'), t('vipBenefitBonus10')],
    },
    vip_yearly: {
      name: t('vipAnnualName') || 'VIP Annuel',
      benefits: [t('vipBenefitAnnual1'), t('vipBenefitPriority'), t('vipBenefitExclusive'), t('vipBenefitBonus15'), t('vipBenefitBirthday')],
    },
  };
  const subPlanLocalised = subscription ? subscriptionPlanTranslations[subscription.plan_id] || subscriptionPlanTranslations[subscription.plan_details?.id] : null;

  if (!user) return null;

  return (
    <div className="min-h-screen bg-rolex-green">
      {/* Hero header */}
      <section className="relative overflow-hidden border-b border-[hsl(var(--gold)/0.18)]">
        <div className="absolute inset-0 hero-spotlight pointer-events-none" />
        <div className="absolute inset-0 noise-overlay opacity-50 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative">
          <p className="gold-line mb-3"><Sparkles className="w-3 h-3" /> <InlineEditable tKey="myAccount">{t('myAccount') || 'My Account'}</InlineEditable></p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-cream">
            <InlineEditable tKey="welcomeBack">{t('welcomeBack') || 'Welcome back'}</InlineEditable>, <span className="text-gold-gradient">{user.first_name}</span>
          </h1>
          <p className="text-cream/60 mt-2 text-sm">{user.email}</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Profile sidebar — luxury minimal */}
          <div className="lg:col-span-1" data-testid="account-profile-card">
            <div className="rounded-3xl bg-gradient-to-b from-[#FDF7E8] via-[#F5EFE0] to-[#EFE7D2] border border-[hsl(var(--gold)/0.30)] overflow-hidden gold-glow shadow-[0_20px_50px_rgba(11,61,46,0.30)]">
              <div className="px-7 pt-9 pb-7 flex flex-col items-center text-center" style={{ color: '#0B3D2E' }}>
                {/* Avatar with gold ring */}
                <div className="relative mb-5">
                  <div className="absolute inset-0 rounded-full bg-gold-gradient blur-md opacity-40" />
                  <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-[#0E4936] to-[#08291F] flex items-center justify-center ring-[3px] ring-[hsl(var(--gold))] shadow-[0_10px_28px_rgba(0,0,0,0.25)] overflow-hidden">
                    {user.picture ? (
                      <img src={user.picture} alt={user.first_name || 'Avatar'} referrerPolicy="no-referrer" className="w-full h-full object-cover" data-testid="profile-avatar-img" />
                    ) : (
                      <span className="font-display text-4xl font-extrabold text-gold-gradient">
                        {(user.first_name?.[0] || 'U').toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Name */}
                <h2 className="font-display text-2xl font-extrabold leading-tight" style={{ color: '#0B3D2E' }}>
                  {user.first_name} {user.last_name}
                </h2>
                <p className="text-xs mt-1 mb-4 font-medium" style={{ color: '#456B5B' }}>{user.email}</p>

                {/* VIP badge — shows ACTIVE SUBSCRIPTION badge if subscribed, otherwise loyalty level */}
                {subscription && subscription.active ? (
                  <div className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gold-gradient shadow-md ring-2 ring-rolex-green/30 animate-pulse-gold" style={{ color: '#0B3D2E' }} data-testid="vip-active-badge">
                    <Crown className="w-4 h-4" />
                    <span className="text-[11px] font-extrabold tracking-[0.22em] uppercase">
                      VIP {subPlanLocalised?.name || subscription.plan_name || subscription.plan_details?.name || ''}
                    </span>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[hsl(var(--gold)/0.55)] bg-[hsl(var(--gold)/0.15)]" style={{ color: '#8B6620' }}>
                    <Crown className="w-3.5 h-3.5" style={{ color: '#8B6620' }} />
                    <span className="text-[10px] font-bold tracking-[0.22em] uppercase" style={{ color: '#8B6620' }}>{vipLabels[user.vip_level || 0]} {t('member') || 'Member'}</span>
                  </div>
                )}

                {/* Decorative gold divider */}
                <div className="divider-gold-thick w-full max-w-[200px] my-7" />

                {/* Big stats */}
                <div className="w-full grid grid-cols-2 gap-0">
                  <div className="text-center">
                    <p className="font-mono text-4xl font-extrabold text-gold-gradient leading-none tabular-nums">
                      {user.points_balance || 0}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.22em] font-bold mt-2" style={{ color: '#456B5B' }}><InlineEditable tKey="points">{t('points')}</InlineEditable></p>
                  </div>
                  <div className="text-center border-l border-[hsl(var(--gold)/0.30)]">
                    <p className="font-mono text-4xl font-extrabold leading-none tabular-nums" style={{ color: '#0B3D2E' }}>
                      {user.total_entries || 0}
                    </p>
                    <p className="text-[10px] uppercase tracking-[0.22em] font-bold mt-2" style={{ color: '#456B5B' }}><InlineEditable tKey="entries">{t('entries')}</InlineEditable></p>
                  </div>
                </div>

                <div className="divider-gold w-full max-w-[200px] my-7" />

                {/* Referral block */}
                {user.referral_code && (
                  <div className="w-full mb-4">
                    <p className="text-[9px] uppercase tracking-[0.28em] font-bold mb-2" style={{ color: '#456B5B' }}><InlineEditable tKey="referralCode">{t('referralCode')}</InlineEditable></p>
                    <button
                      type="button"
                      onClick={copyReferral}
                      className="group w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-[hsl(var(--gold)/0.55)] bg-white/60 hover:bg-[hsl(var(--gold)/0.08)] hover:border-gold transition-all"
                    >
                      <span className="font-mono font-bold text-base tracking-[0.15em]" style={{ color: '#0B3D2E' }}>{user.referral_code}</span>
                      {copied ? <Check className="w-4 h-4" style={{ color: '#15803D' }} /> : <Copy className="w-4 h-4 group-hover:!text-gold-dark transition-colors" style={{ color: '#456B5B' }} />}
                    </button>
                  </div>
                )}

                {!user.referred_by && (
                  <div className="w-full mb-5">
                    <p className="text-[9px] uppercase tracking-[0.28em] font-bold mb-2 flex items-center justify-center gap-1.5" style={{ color: '#456B5B' }}>
                      <UserPlus className="w-3 h-3" /> <InlineEditable tKey="applyReferral">{t('applyReferral')}</InlineEditable>
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={referralInput}
                        onChange={(e) => setReferralInput(e.target.value)}
                        placeholder={t('referralPlaceholder')}
                        className="rounded-lg text-sm h-9 bg-white/70 border-[hsl(var(--gold)/0.35)]"
                        style={{ color: '#0B3D2E' }}
                      />
                      <Button size="sm" className="btn-gold rounded-lg text-xs h-9 px-3 font-bold shrink-0" onClick={handleApplyReferral} disabled={applyingReferral}><InlineEditable tKey="apply">{t('apply')}</InlineEditable></Button>
                    </div>
                  </div>
                )}

                <Button
                  variant="outline"
                  style={{ color: '#0B3D2E' }}
                  className="w-full rounded-xl h-11 border border-[hsl(var(--gold)/0.50)] bg-transparent hover:bg-[hsl(var(--gold)/0.10)] hover:border-gold font-semibold text-xs tracking-[0.15em] uppercase mt-1"
                  onClick={() => { logout(); navigate('/'); }}
                  data-testid="account-logout-button"
                >
                  <LogOut className="w-3.5 h-3.5 mr-2" /> <InlineEditable tKey="signOut">{t('signOut')}</InlineEditable>
                </Button>
              </div>
            </div>
          </div>

          {/* Tabs main area */}
          <div className="lg:col-span-2">
            {profileIncomplete && (
              <div className="mb-5 rounded-2xl border border-[hsl(var(--gold)/0.4)] bg-gradient-to-r from-[hsl(var(--gold)/0.14)] to-[hsl(var(--gold)/0.05)] p-4 flex items-center gap-4" data-testid="complete-profile-banner">
                <div className="shrink-0 w-11 h-11 rounded-full bg-gold-gradient flex items-center justify-center text-rolex-green shadow-md">
                  <UserCog className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-cream text-sm leading-tight">
                    <InlineEditable tKey="completeProfileTitle">{t('completeProfileTitle') || 'Complétez votre profil'}</InlineEditable>
                  </p>
                  <p className="text-xs text-cream/70 mt-0.5">
                    <InlineEditable tKey="completeProfileDesc" multiline>{t('completeProfileDesc') || 'Ajoutez votre téléphone et votre adresse pour finaliser vos participations et l\u2019envoi des lots.'}</InlineEditable>
                  </p>
                </div>
                <Button size="sm" className="btn-gold rounded-xl shrink-0 font-bold" onClick={() => setActiveTab('profile')} data-testid="complete-profile-btn">
                  <InlineEditable tKey="completeProfileCta">{t('completeProfileCta') || 'Compléter'}</InlineEditable>
                </Button>
              </div>
            )}
            <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="account-tabs">
              <TabsList className="bg-[hsl(var(--rolex-green-elev)/0.5)] backdrop-blur-sm border border-[hsl(var(--gold)/0.20)] mb-5 flex-wrap p-1.5 h-auto">
                <TabsTrigger value="entries" className="gap-2 data-[state=active]:bg-[hsl(var(--gold)/0.15)] data-[state=active]:!text-gold text-cream/70"><Trophy className="w-4 h-4" /> <InlineEditable tKey="myEntries">{t('myEntries')}</InlineEditable></TabsTrigger>
                <TabsTrigger value="payments" className="gap-2 data-[state=active]:bg-[hsl(var(--gold)/0.15)] data-[state=active]:!text-gold text-cream/70"><CreditCard className="w-4 h-4" /> <InlineEditable tKey="myPayments">{t('myPayments') || 'Mes paiements'}</InlineEditable></TabsTrigger>
                <TabsTrigger value="subscription" className="gap-2 data-[state=active]:bg-[hsl(var(--gold)/0.15)] data-[state=active]:!text-gold text-cream/70 relative">
                  <Crown className="w-4 h-4" />
                  <InlineEditable tKey="mySubscription">{t('mySubscription') || 'Mon abonnement'}</InlineEditable>
                  {subscription?.active && <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-gold animate-pulse" title="Active" />}
                </TabsTrigger>
                <TabsTrigger value="points" className="gap-2 data-[state=active]:bg-[hsl(var(--gold)/0.15)] data-[state=active]:!text-gold text-cream/70"><History className="w-4 h-4" /> <InlineEditable tKey="pointsHistory">{t('pointsHistory')}</InlineEditable></TabsTrigger>
                <TabsTrigger value="wins" className="gap-2 data-[state=active]:bg-[hsl(var(--gold)/0.15)] data-[state=active]:!text-gold text-cream/70"><Gift className="w-4 h-4" /> <InlineEditable tKey="myWins">{t('myWins')}</InlineEditable></TabsTrigger>
                <TabsTrigger value="notifications" className="gap-2 data-[state=active]:bg-[hsl(var(--gold)/0.15)] data-[state=active]:!text-gold text-cream/70"><Bell className="w-4 h-4" /> <InlineEditable tKey="notifications">{t('notifications')}</InlineEditable></TabsTrigger>
                <TabsTrigger value="profile" className="gap-2 data-[state=active]:bg-[hsl(var(--gold)/0.15)] data-[state=active]:!text-gold text-cream/70"><Settings className="w-4 h-4" /> <InlineEditable tKey="profile">{t('profile')}</InlineEditable></TabsTrigger>
              </TabsList>

              <TabsContent value="entries">
                <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]"><CardContent className="p-0">
                  {entries.length === 0 ? <div className="p-10 text-center text-rolex-green/55"><Trophy className="w-10 h-10 mx-auto mb-3 opacity-40 text-gold" /><p className="font-semibold"><InlineEditable tKey="noEntries">{t('noEntries')}</InlineEditable></p></div> : (
                    <Table><TableHeader><TableRow><TableHead><InlineEditable tKey="draw">{t('draw')}</InlineEditable></TableHead><TableHead><InlineEditable tKey="entries">{t('entries')}</InlineEditable></TableHead><TableHead><InlineEditable tKey="paymentMethod">{t('paymentMethod') || 'Paiement'}</InlineEditable></TableHead><TableHead><InlineEditable tKey="cost">{t('cost') || 'Coût'}</InlineEditable></TableHead><TableHead className="text-right"><InlineEditable tKey="date">{t('date')}</InlineEditable></TableHead></TableRow></TableHeader>
                    <TableBody>{entries.map((e) => {
                      const isStripe = !!e.stripe_session_id || (e.amount_paid_gbp > 0);
                      const method = e.payment_method || (isStripe ? 'card' : 'credit');
                      const isCrypto = method === 'crypto';
                      const isManual = method === 'manual';
                      const payCur = (e.pay_currency || '').toUpperCase();
                      return (
                        <TableRow
                          key={e._id}
                          className="cursor-pointer hover:bg-[hsl(var(--gold)/0.10)] transition-colors"
                          onClick={() => setTicketsDialog({ open: true, entryId: e._id })}
                          data-testid={`entry-row-${e._id}`}
                        >
                          <TableCell className="font-medium flex items-center gap-1.5">
                            {e.raffle?.title || 'N/A'}
                            <Ticket className="w-3.5 h-3.5 text-gold opacity-60" />
                          </TableCell>
                          <TableCell><Badge variant="outline" className="font-mono">x{e.quantity}</Badge></TableCell>
                          <TableCell>
                            {isCrypto ? (
                              <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-[10px] gap-1">🪙 Crypto{payCur && ` · ${payCur}`}</Badge>
                            ) : isManual ? (
                              <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-[10px] gap-1">✨ Manuel</Badge>
                            ) : isStripe ? (
                              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] gap-1">💳 <InlineEditable tKey="paidWithCard">{t('paidWithCard') || 'Carte'}</InlineEditable></Badge>
                            ) : (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] gap-1">⭐ <InlineEditable tKey="paidWithPoints">{t('paidWithPoints') || 'Points'}</InlineEditable></Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-mono font-bold">
                            {isStripe ? (
                              <span className={isCrypto ? 'text-orange-700' : 'text-emerald-700'}>£{Number(e.amount_paid_gbp || 0).toFixed(2)}</span>
                            ) : (
                              <span className="text-red-600">-£{e.points_spent}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-xs text-rolex-green/55">{new Date(e.created_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      );
                    })}</TableBody></Table>
                  )}
                </CardContent></Card>
                {entries.length > 0 && (
                  <p className="text-[11px] text-cream/55 mt-2 px-2">💡 <InlineEditable tKey="clickToSeeTickets">{t('clickToSeeTickets') || 'Click any row to see your ticket numbers'}</InlineEditable></p>
                )}
              </TabsContent>

              {/* MY PAYMENTS TAB — all Stripe transactions (point packs, VIP, direct tickets) */}
              <TabsContent value="payments">
                {/* Summary cards */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]"><CardContent className="p-4 text-center">
                    <CreditCard className="w-5 h-5 text-emerald-700 mx-auto mb-1" />
                    <p className="font-mono text-xl font-bold text-emerald-700">£{payments.filter(p => p.fulfilled).reduce((s, p) => s + Number(p.amount_gbp || 0), 0).toFixed(2)}</p>
                    <p className="text-[10px] uppercase tracking-wider text-rolex-green/60 mt-0.5"><InlineEditable tKey="totalPaid">{t('totalPaid') || 'Total payé'}</InlineEditable></p>
                  </CardContent></Card>
                  <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]"><CardContent className="p-4 text-center">
                    <Ticket className="w-5 h-5 text-gold mx-auto mb-1" />
                    <p className="font-mono text-xl font-bold text-gold-dark">{payments.filter(p => p.fulfilled && p.type === 'raffle_direct').length}</p>
                    <p className="text-[10px] uppercase tracking-wider text-rolex-green/60 mt-0.5"><InlineEditable tKey="ticketPurchases">{t('ticketPurchases') || 'Achats tickets'}</InlineEditable></p>
                  </CardContent></Card>
                  <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]"><CardContent className="p-4 text-center">
                    <Coins className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                    <p className="font-mono text-xl font-bold text-blue-600">{payments.filter(p => p.fulfilled && (p.type === 'point_pack' || p.type === 'vip')).length}</p>
                    <p className="text-[10px] uppercase tracking-wider text-rolex-green/60 mt-0.5"><InlineEditable tKey="otherPurchases">{t('otherPurchases') || 'Packs & VIP'}</InlineEditable></p>
                  </CardContent></Card>
                </div>

                <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]"><CardContent className="p-0">
                  {payments.length === 0 ? (
                    <div className="p-10 text-center text-rolex-green/55">
                      <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-40 text-gold" />
                      <p className="font-semibold"><InlineEditable tKey="noPayments">{t('noPayments') || 'Aucun paiement par carte'}</InlineEditable></p>
                      <p className="text-xs mt-1"><InlineEditable tKey="noPaymentsHint">{t('noPaymentsHint') || 'Vos paiements par carte apparaîtront ici.'}</InlineEditable></p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead><InlineEditable tKey="date">{t('date')}</InlineEditable></TableHead>
                          <TableHead><InlineEditable tKey="type">{t('type')}</InlineEditable></TableHead>
                          <TableHead><InlineEditable tKey="description">{t('description')}</InlineEditable></TableHead>
                          <TableHead className="text-right"><InlineEditable tKey="amount">{t('amount')}</InlineEditable></TableHead>
                          <TableHead className="text-center"><InlineEditable tKey="status">{t('status')}</InlineEditable></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payments.map((p) => {
                          const meta = p.metadata || {};
                          let typeLabel, typeIcon, desc;
                          if (p.type === 'point_pack') {
                            typeIcon = <Coins className="w-3.5 h-3.5" />;
                            typeLabel = t('pointPack') || 'Pack de points';
                            desc = `+${meta.points_to_credit || '?'} pts`;
                          } else if (p.type === 'vip') {
                            typeIcon = <Crown className="w-3.5 h-3.5" />;
                            typeLabel = t('vipSubscription2') || 'Abonnement VIP';
                            desc = meta.plan_name || 'VIP';
                          } else if (p.type === 'raffle_direct') {
                            typeIcon = <Ticket className="w-3.5 h-3.5" />;
                            typeLabel = t('raffleTickets') || 'Tickets tirage';
                            desc = `${p.raffle_title || meta.raffle_id || '?'} (x${meta.quantity || 1})`;
                          } else {
                            typeIcon = <CreditCard className="w-3.5 h-3.5" />;
                            typeLabel = p.type;
                            desc = '';
                          }
                          const statusBadge = p.fulfilled ? (
                            <Badge className="bg-emerald-500 text-white border-none text-[10px] gap-1"><Check className="w-3 h-3" /> {t('paid') || 'Payé'}</Badge>
                          ) : p.payment_status === 'unpaid' ? (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">{t('pending') || 'En attente'}</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px]">{p.status || 'init'}</Badge>
                          );
                          return (
                            <TableRow key={p._id}>
                              <TableCell className="text-xs text-rolex-green/55 whitespace-nowrap font-mono">
                                {new Date(p.created_at).toLocaleDateString()}<br/>
                                <span className="text-[10px]">{new Date(p.created_at).toLocaleTimeString()}</span>
                              </TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-[10px] gap-1">{typeIcon} {typeLabel}</Badge>
                              </TableCell>
                              <TableCell className="text-sm max-w-[260px] truncate" title={desc}>{desc}</TableCell>
                              <TableCell className="text-right font-mono font-bold text-emerald-700">
                                £{Number(p.amount_gbp || 0).toFixed(2)}
                              </TableCell>
                              <TableCell className="text-center">{statusBadge}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </CardContent></Card>
                {payments.length > 0 && (
                  <p className="text-[11px] text-cream/55 mt-2 px-2">
                    💡 <InlineEditable tKey="paymentsHint">{t('paymentsHint') || 'Tous les paiements par carte (point packs, VIP, tickets directs) sont listés ici.'}</InlineEditable>
                  </p>
                )}
              </TabsContent>

              {/* MY SUBSCRIPTION TAB */}
              <TabsContent value="subscription">
                {subscription && subscription.active ? (
                  <div className="space-y-5">
                    {/* Active VIP card - hero */}
                    <Card className="relative overflow-hidden border-2 border-gold shadow-2xl">
                      <div className="absolute inset-0 bg-gradient-to-br from-rolex-green via-[#0E4936] to-[#08291F]" />
                      <div className="absolute inset-0 hero-spotlight opacity-40 pointer-events-none" />
                      <CardContent className="relative p-8 z-10">
                        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-full bg-gold-gradient flex items-center justify-center shadow-2xl">
                              <Crown className="w-8 h-8 text-rolex-green" strokeWidth={2.5} />
                            </div>
                            <div>
                              <p className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: '#D4AF37' }}>
                                <InlineEditable tKey="activeSubscription">{t('activeSubscription') || 'Abonnement Actif'}</InlineEditable>
                              </p>
                              <h2 className="font-display text-3xl font-black" style={{ color: '#F5EFE0' }}>
                                VIP {subPlanLocalised?.name || subscription.plan_name || subscription.plan_details?.name || ''}
                              </h2>
                            </div>
                          </div>
                          <Badge className="bg-emerald-500 text-white border-none text-xs px-3 py-1.5">
                            <Check className="w-3 h-3 mr-1" />
                            <InlineEditable tKey="active">{t('active') || 'Actif'}</InlineEditable>
                          </Badge>
                        </div>

                        {/* Stats grid - solid backgrounds */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                          <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(245, 239, 224, 0.95)', border: '1px solid rgba(212, 175, 55, 0.5)' }}>
                            <Coins className="w-5 h-5 mx-auto mb-1" style={{ color: '#A07828' }} />
                            <p className="font-mono text-2xl font-black" style={{ color: '#0B3D2E' }}>
                              +{subscription.plan_details?.points_per_month || 0}
                            </p>
                            <p className="text-[9px] uppercase tracking-wider mt-0.5 font-bold" style={{ color: '#456B5B' }}>
                              <InlineEditable tKey="pointsPerMonth">{t('pointsPerMonth') || 'Points / mois'}</InlineEditable>
                            </p>
                          </div>
                          <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(245, 239, 224, 0.95)', border: '1px solid rgba(212, 175, 55, 0.5)' }}>
                            <CreditCard className="w-5 h-5 mx-auto mb-1" style={{ color: '#A07828' }} />
                            <p className="font-mono text-2xl font-black" style={{ color: '#0B3D2E' }}>
                              £{subscription.plan_details?.price_gbp || 0}
                            </p>
                            <p className="text-[9px] uppercase tracking-wider mt-0.5 font-bold" style={{ color: '#456B5B' }}>
                              {subscription.plan_details?.billing_cycle === 'yearly' ? <><InlineEditable tKey="perYear">{t('perYear') || 'Par an'}</InlineEditable></> : <><InlineEditable tKey="perMonth2">{t('perMonth2') || 'Par mois'}</InlineEditable></>}
                            </p>
                          </div>
                          <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(245, 239, 224, 0.95)', border: '1px solid rgba(212, 175, 55, 0.5)' }}>
                            <History className="w-5 h-5 mx-auto mb-1" style={{ color: '#A07828' }} />
                            <p className="font-mono text-base font-black" style={{ color: '#0B3D2E' }}>
                              {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : '—'}
                            </p>
                            <p className="text-[9px] uppercase tracking-wider mt-0.5 font-bold" style={{ color: '#456B5B' }}>
                              <InlineEditable tKey="renewsOn">{t('renewsOn') || 'Renouvellement'}</InlineEditable>
                            </p>
                          </div>
                        </div>

                        {/* Benefits */}
                        {(subPlanLocalised?.benefits || subscription.plan_details?.benefits || []).length > 0 && (
                          <div className="rounded-xl p-5 mb-6" style={{ background: 'rgba(245, 239, 224, 0.95)', border: '1px solid rgba(212, 175, 55, 0.5)' }}>
                            <p className="text-[10px] uppercase tracking-[0.25em] font-bold mb-3" style={{ color: '#A07828' }}>
                              <InlineEditable tKey="yourBenefits">{t('yourBenefits') || 'Vos avantages'}</InlineEditable>
                            </p>
                            <ul className="space-y-2">
                              {(subPlanLocalised?.benefits || subscription.plan_details?.benefits || []).map((b, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#0B3D2E' }}>
                                  <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#A07828' }} />
                                  <span>{b}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Started date */}
                        {subscription.started_at && (
                          <p className="text-xs text-center mb-4" style={{ color: 'rgba(245, 239, 224, 0.75)' }}>
                            <InlineEditable tKey="memberSince">{t('memberSince') || 'Membre depuis'}</InlineEditable> {new Date(subscription.started_at).toLocaleDateString()}
                          </p>
                        )}
                      </CardContent>
                    </Card>

                    {/* Management actions */}
                    <Card className="bg-cream border border-gold/30">
                      <CardContent className="p-5 space-y-3">
                        <div className="flex flex-col sm:flex-row gap-3">
                          <Button
                            onClick={() => navigate('/vip')}
                            variant="outline"
                            className="flex-1 border-gold/50 text-gold-dark hover:bg-gold/10 gap-2 h-12"
                            data-testid="change-plan-btn"
                          >
                            <Crown className="w-4 h-4" />
                            <InlineEditable tKey="changePlan">{t('changePlan') || 'Changer de plan'}</InlineEditable>
                          </Button>
                          <Button
                            onClick={handleCancelSubscription}
                            disabled={cancellingSubscription}
                            variant="outline"
                            className="flex-1 border-red-300 text-red-600 hover:bg-red-50 gap-2 h-12"
                            data-testid="cancel-subscription-btn"
                          >
                            {cancellingSubscription ? <span>...</span> : (
                              <>
                                <ArrowDownRight className="w-4 h-4" />
                                <InlineEditable tKey="cancelSubscription">{t('cancelSubscription') || 'Annuler l\u2019abonnement'}</InlineEditable>
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-[11px] text-rolex-green/55 text-center pt-2">
                          💡 <InlineEditable tKey="cancelHint">{t('cancelHint') || 'Si vous annulez, vous gardez vos avantages VIP jusqu\u2019à la fin de la période payée.'}</InlineEditable>
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  /* No active subscription */
                  <Card className="bg-cream border border-gold/20">
                    <CardContent className="p-10 text-center">
                      <Crown className="w-16 h-16 mx-auto mb-4 text-gold/40" />
                      <h3 className="font-display text-xl font-bold text-rolex-green mb-2">
                        <InlineEditable tKey="noActiveSubscription">{t('noActiveSubscription') || 'Aucun abonnement actif'}</InlineEditable>
                      </h3>
                      <p className="text-rolex-green/65 mb-6 max-w-md mx-auto">
                        <InlineEditable tKey="vipPromo" multiline>{t('vipPromo') || 'Devenez VIP pour profiter de points mensuels, accès prioritaire et avantages exclusifs.'}</InlineEditable>
                      </p>
                      <Button onClick={() => navigate('/vip')} className="btn-gold gap-2 px-6 h-12" data-testid="discover-vip-btn">
                        <Sparkles className="w-4 h-4" />
                        <InlineEditable tKey="discoverVip">{t('discoverVip') || 'Découvrir les abonnements VIP'}</InlineEditable>
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="points">
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]"><CardContent className="p-4 text-center">
                    <ArrowUpRight className="w-5 h-5 text-green-700 mx-auto mb-1" />
                    <p className="font-mono text-xl font-bold text-green-700">{transactions.filter(tx => tx.amount > 0).reduce((s, tx) => s + tx.amount, 0)}</p>
                    <p className="text-[10px] uppercase tracking-wider text-rolex-green/60 mt-0.5"><InlineEditable tKey="totalEarned">{t('totalEarned')}</InlineEditable></p>
                  </CardContent></Card>
                  <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]"><CardContent className="p-4 text-center">
                    <ArrowDownRight className="w-5 h-5 text-red-500 mx-auto mb-1" />
                    <p className="font-mono text-xl font-bold text-red-500">{Math.abs(transactions.filter(tx => tx.amount < 0).reduce((s, tx) => s + tx.amount, 0))}</p>
                    <p className="text-[10px] uppercase tracking-wider text-rolex-green/60 mt-0.5"><InlineEditable tKey="totalSpentPts">{t('totalSpentPts')}</InlineEditable></p>
                  </CardContent></Card>
                  <Card className="bg-cream border-2 border-[hsl(var(--gold)/0.45)] gold-glow"><CardContent className="p-4 text-center">
                    <Coins className="w-5 h-5 text-gold mx-auto mb-1" />
                    <p className="font-mono text-xl font-bold text-gold-dark">{user.points_balance || 0}</p>
                    <p className="text-[10px] uppercase tracking-wider text-rolex-green/60 mt-0.5"><InlineEditable tKey="currentBalanceShort">{t('currentBalanceShort')}</InlineEditable></p>
                  </CardContent></Card>
                </div>
                <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]"><CardContent className="p-0">
                  {transactions.length === 0 ? <div className="p-10 text-center text-rolex-green/55"><History className="w-10 h-10 mx-auto mb-3 opacity-40 text-gold" /><p className="font-semibold"><InlineEditable tKey="noTransactions">{t('noTransactions')}</InlineEditable></p></div> : (
                    <Table><TableHeader><TableRow><TableHead><InlineEditable tKey="date">{t('date')}</InlineEditable></TableHead><TableHead><InlineEditable tKey="type">{t('type')}</InlineEditable></TableHead><TableHead><InlineEditable tKey="description">{t('description')}</InlineEditable></TableHead><TableHead className="text-right"><InlineEditable tKey="amount">{t('amount')}</InlineEditable></TableHead></TableRow></TableHeader>
                    <TableBody>{transactions.map((tx) => {
                      const typeLabels = { admin_credit: t('adminCredit'), admin_debit: t('adminDebit'), entry: t('raffleEntry'), spin_bonus: t('dailySpinBonus'), referral_bonus: t('referralBonusTx'), signup_bonus: t('signupBonusTx'), share_bonus: t('shareBonusTx'), stripe_pack: t('stripePack') || '💳 Achat Stripe', vip_subscription: t('vipSubscription') || '👑 Abonnement VIP' };
                      const typeColors = { admin_credit: 'bg-blue-50 text-blue-700 border-blue-200', admin_debit: 'bg-red-50 text-red-600 border-red-200', entry: 'bg-orange-50 text-orange-700 border-orange-200', spin_bonus: 'bg-purple-50 text-purple-700 border-purple-200', referral_bonus: 'bg-green-50 text-green-700 border-green-200', signup_bonus: 'bg-emerald-50 text-emerald-700 border-emerald-200', share_bonus: 'bg-pink-50 text-pink-700 border-pink-200', stripe_pack: 'bg-emerald-50 text-emerald-700 border-emerald-300', vip_subscription: 'bg-gold/15 text-gold-dark border-gold/40' };
                      // Localize description based on tx.type (parses legacy English text from backend)
                      const desc = tx.description || '';
                      let localizedDesc = desc;
                      if (tx.type === 'signup_bonus') {
                        localizedDesc = t('txDescSignupBonus');
                      } else if (tx.type === 'entry') {
                        const m = desc.match(/Entry x(\d+)\s*-\s*(.+)/);
                        if (m) {
                          localizedDesc = t('txDescEntry').replace('{{qty}}', m[1]).replace('{{raffle}}', m[2]);
                        }
                      } else if (tx.type === 'referral_bonus') {
                        const mc = desc.match(/Referral bonus \(code:\s*(.+?)\)/);
                        const mr = desc.match(/Referral bonus \(referred:\s*(.+?)\)/);
                        if (mc) {
                          localizedDesc = t('txDescReferralBonusCode').replace('{{code}}', mc[1]);
                        } else if (mr) {
                          localizedDesc = t('txDescReferralBonusReferred').replace('{{email}}', mr[1]);
                        }
                      } else if (tx.type === 'spin_bonus') {
                        const m = desc.match(/(\d+)\s*pts/);
                        if (m) {
                          localizedDesc = t('txDescSpinReward').replace('{{reward}}', m[1]);
                        }
                      } else if (tx.type === 'share_bonus') {
                        localizedDesc = t('txDescShareBonus');
                      } else if (tx.type === 'admin_credit' && /admin credit/i.test(desc)) {
                        localizedDesc = t('txDescAdminCredit');
                      } else if (tx.type === 'admin_debit' && /admin debit/i.test(desc)) {
                        localizedDesc = t('txDescAdminDebit');
                      }
                      return (
                        <TableRow key={tx._id}>
                          <TableCell className="text-xs text-rolex-green/55 whitespace-nowrap">{new Date(tx.created_at).toLocaleDateString()}<br/><span className="text-[10px]">{new Date(tx.created_at).toLocaleTimeString()}</span></TableCell>
                          <TableCell><Badge variant="outline" className={`text-[10px] ${typeColors[tx.type] || ''}`}>{typeLabels[tx.type] || tx.type}</Badge></TableCell>
                          <TableCell className="text-sm max-w-[220px] truncate" title={localizedDesc}>{localizedDesc}</TableCell>
                          <TableCell className={`text-right font-mono font-bold ${tx.amount > 0 ? 'text-green-700' : 'text-red-500'}`}>{tx.amount > 0 ? '+' : ''}{tx.amount}</TableCell>
                        </TableRow>
                      );
                    })}</TableBody></Table>
                  )}
                </CardContent></Card>
              </TabsContent>

              <TabsContent value="wins">
                <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]"><CardContent className="p-0">
                  {wins.length === 0 ? <div className="p-10 text-center text-rolex-green/55"><Gift className="w-10 h-10 mx-auto mb-3 opacity-40 text-gold" /><p className="font-semibold"><InlineEditable tKey="noWins">{t('noWins')}</InlineEditable></p></div> : (
                    <Table><TableHeader><TableRow><TableHead><InlineEditable tKey="draw">{t('draw')}</InlineEditable></TableHead><TableHead><InlineEditable tKey="value">{t('value')}</InlineEditable></TableHead><TableHead className="text-right"><InlineEditable tKey="date">{t('date')}</InlineEditable></TableHead></TableRow></TableHeader>
                    <TableBody>{wins.map((w) => (<TableRow key={w._id}><TableCell className="font-medium">{w.raffle_title}</TableCell><TableCell className="font-mono text-gold-dark font-bold">£{w.product_value?.toLocaleString()}</TableCell><TableCell className="text-right text-xs text-rolex-green/55">{w.drawn_at ? new Date(w.drawn_at).toLocaleDateString() : ''}</TableCell></TableRow>))}</TableBody></Table>
                  )}
                </CardContent></Card>
              </TabsContent>

              <TabsContent value="notifications">
                <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]"><CardContent className="p-0">
                  {notifications.length === 0 ? <div className="p-10 text-center text-rolex-green/55"><Bell className="w-10 h-10 mx-auto mb-3 opacity-40 text-gold" /><p className="font-semibold"><InlineEditable tKey="noNotifications">{t('noNotifications')}</InlineEditable></p></div> : (
                    <div className="divide-y divide-[hsl(var(--gold)/0.15)]">
                      {notifications.map((n) => {
                        const isWinner = n.type === 'winner_announcement';
                        const d = n.data || {};
                        const raffleTitle = d.raffle_title || '';
                        const productValue = Number(d.product_value || 0).toLocaleString();
                        // Localize title/message based on notification type. Fallback to backend strings if data missing.
                        let title = n.title;
                        let message = n.message;
                        if (isWinner && raffleTitle) {
                          title = `🏆 $<InlineEditable tKey="winnerNotifTitle">{t('winnerNotifTitle')}</InlineEditable> ${raffleTitle}!`;
                          message = t('winnerNotifMessage').replace('{{raffle}}', raffleTitle).replace('{{value}}', productValue);
                        } else if (n.type === 'entry' && raffleTitle) {
                          title = t('notifEntryTitle');
                          message = t('notifEntryMessage').replace('{{raffle}}', raffleTitle).replace('{{qty}}', d.quantity || '');
                        } else if (n.type === 'new_raffle' && raffleTitle) {
                          title = t('notifNewRaffleTitle');
                          message = t('notifNewRaffleMessage').replace('{{raffle}}', raffleTitle).replace('{{value}}', productValue);
                        } else if (n.type === 'spin') {
                          title = t('notifSpinTitle');
                          message = t('notifSpinMessage').replace('{{reward}}', d.reward != null ? d.reward : (n.message || '').replace(/[^0-9]/g, ''));
                        } else if (n.type === 'testimonial_approved' && raffleTitle) {
                          title = t('notifTestimonialApprovedTitle');
                          message = t('notifTestimonialApprovedMessage').replace('{{raffle}}', raffleTitle);
                        } else if (n.type === 'testimonial_rejected' && raffleTitle) {
                          title = t('notifTestimonialRejectedTitle');
                          message = t('notifTestimonialRejectedMessage').replace('{{raffle}}', raffleTitle);
                        } else if (n.type === 'testimonial_pending') {
                          title = t('notifTestimonialPendingTitle');
                          message = t('notifTestimonialPendingMessage')
                            .replace('{{firstName}}', d.user_first_name || '')
                            .replace('{{lastName}}', d.user_last_name || '')
                            .replace('{{raffle}}', raffleTitle);
                        }
                        return (
                          <div key={n._id} className={`p-4 ${isWinner ? 'bg-gold-gradient/15 border-l-4 border-gold' : (!n.read ? 'bg-[hsl(var(--gold)/0.06)]' : '')}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-3 flex-1 min-w-0">
                                {isWinner && (
                                  <div className="w-10 h-10 rounded-full bg-gold-gradient flex items-center justify-center shrink-0 shadow-md">
                                    <Trophy className="w-5 h-5 text-rolex-green" />
                                  </div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <p className="font-semibold text-sm text-rolex-green flex items-center gap-1.5">
                                    {title}
                                    {!n.read && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" title={t('unread') || 'Unread'} />}
                                  </p>
                                  <p className="text-sm text-rolex-green/75 mt-1">{message}</p>
                                  {isWinner && n.data?.image && (
                                    <img src={n.data.image} alt="" className="mt-3 rounded-lg max-w-[200px] h-24 object-cover border border-[hsl(var(--gold)/0.4)]" />
                                  )}
                                  <p className="text-xs text-rolex-green/45 mt-2">{new Date(n.created_at).toLocaleString()}</p>
                                </div>
                              </div>
                              {!n.read && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-xs !text-gold-dark hover:bg-[hsl(var(--gold)/0.10)] shrink-0"
                                  onClick={async () => { await markNotificationRead(n._id); setNotifications(prev => prev.map(x => x._id === n._id ? {...x, read: true} : x)); }}
                                >
                                  <InlineEditable tKey="markRead">{t('markRead') || 'Mark read'}</InlineEditable>
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent></Card>
              </TabsContent>

              <TabsContent value="profile">
                <Card className="bg-cream border border-[hsl(var(--gold)/0.20)]"><CardHeader><div className="flex items-center justify-between"><CardTitle className="text-lg text-rolex-green"><InlineEditable tKey="profileDetails">{t('profileDetails')}</InlineEditable></CardTitle><Button size="sm" className="btn-gold rounded-xl" onClick={handleSave} disabled={saving} data-testid="profile-save-btn">{saving ? t('saving') : (t('saveChanges') || 'Enregistrer les modifications')}</Button></div></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4"><div className="space-y-2"><Label><InlineEditable tKey="firstName">{t('firstName')}</InlineEditable></Label><Input value={profileForm.first_name} onChange={(e) => setProfileForm({...profileForm, first_name: e.target.value})} className="rounded-xl" /></div><div className="space-y-2"><Label><InlineEditable tKey="lastName">{t('lastName')}</InlineEditable></Label><Input value={profileForm.last_name} onChange={(e) => setProfileForm({...profileForm, last_name: e.target.value})} className="rounded-xl" /></div></div>
                    <div className="space-y-2"><Label><InlineEditable tKey="email">{t('email')}</InlineEditable></Label><Input value={user.email} disabled className="rounded-xl" /></div>
                    <div className="space-y-2"><Label><InlineEditable tKey="phone">{t('phone')}</InlineEditable></Label><PhoneInput value={profileForm.phone} onChange={(v) => setProfileForm({...profileForm, phone: v})} testid="profile-phone" /></div>
                    <div className="space-y-2"><Label><InlineEditable tKey="expAddress">{t('expAddress')}</InlineEditable></Label><Input value={profileForm.address} onChange={(e) => setProfileForm({...profileForm, address: e.target.value})} className="rounded-xl" data-testid="profile-address" /></div>
                    <div className="grid grid-cols-3 gap-4"><div className="space-y-2"><Label><InlineEditable tKey="postcode">{t('postcode')}</InlineEditable></Label><Input value={profileForm.postcode} onChange={(e) => setProfileForm({...profileForm, postcode: e.target.value})} className="rounded-xl" data-testid="profile-postcode" /></div><div className="space-y-2"><Label><InlineEditable tKey="city">{t('city')}</InlineEditable></Label><Input value={profileForm.city} onChange={(e) => setProfileForm({...profileForm, city: e.target.value})} className="rounded-xl" data-testid="profile-city" /></div><div className="space-y-2"><Label><InlineEditable tKey="expCountry">{t('expCountry')}</InlineEditable></Label><Input value={profileForm.country} onChange={(e) => setProfileForm({...profileForm, country: e.target.value})} className="rounded-xl" data-testid="profile-country" /></div></div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
      <Footer />
      <WinnerCelebrationModal
        open={!!celebration}
        notification={celebration}
        onClose={handleCloseCelebration}
      />
      <TicketsPopupDialog
        open={ticketsDialog.open}
        entryId={ticketsDialog.entryId}
        onClose={() => setTicketsDialog({ open: false, entryId: null })}
      />
      <TestimonialPromptDialog
        open={testimonialPrompt.open}
        winner={testimonialPrompt.winner}
        onClose={() => setTestimonialPrompt({ open: false, winner: null })}
        onSubmitted={handleTestimonialSubmitted}
      />
    </div>
  );
}
